import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Archive, Ghost, MoreHorizontal, Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

type ClientOption = {
  readonly id: number
  readonly slug: string
  readonly display_name: string
  readonly color_hex: string
}

type ContactMini = {
  readonly id: number
  readonly name: string
  readonly email: string
}

type AccountMini = {
  readonly id: number
  readonly name: string
  readonly domain: string
}

type OpportunityCard = {
  readonly id: number
  readonly name: string
  readonly value_cents: number
  readonly currency: string
  readonly service_line: string
  readonly coarse_stage: string
  readonly lifecycle_stage_id: string
  readonly status: string
  readonly won: boolean
  readonly stage_age_days: number | null
  readonly contact: ContactMini | null
  readonly account: AccountMini | null
  readonly client: ClientOption
  readonly next_step_note: string | null
  readonly open_task_count: number
}

type StageColumn = {
  readonly key: string
  readonly label: string
  readonly cards: readonly OpportunityCard[]
}

type StageMeta = {
  readonly key: string
  readonly label: string
  readonly lifecycle_ids: readonly string[]
}

type StatusCounts = {
  readonly parked: number
  readonly dead: number
  readonly lost: number
}

type OpportunitiesProps = {
  readonly columns: readonly StageColumn[]
  readonly clients: readonly ClientOption[]
  readonly status_counts: StatusCounts
  readonly filters: {
    readonly client_id: string | null
    readonly q: string
    readonly show_parked: boolean
    readonly show_closed: boolean
  }
  readonly stage_meta: readonly StageMeta[]
}

type PendingDrop = {
  readonly oppId: number
  readonly stage: string
}

export default function Opportunities({ columns, clients, status_counts, filters }: OpportunitiesProps) {
  const description = "Track and move CRM opportunities through deal stages with value and lifecycle tracking."
  const [query, setQuery] = React.useState(filters.q)
  const [pendingDrop, setPendingDrop] = React.useState<PendingDrop | null>(null)
  const [dealValue, setDealValue] = React.useState("")

  const submitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.get("/crm/opportunities", {
      client_id: filters.client_id ?? undefined,
      q: query || undefined,
      show_parked: filters.show_parked ? "1" : undefined,
      show_closed: filters.show_closed ? "1" : undefined,
    }, { preserveState: true, preserveScroll: true })
  }

  const moveCard = (oppId: number, stage: string, valueCents?: number) => {
    const params: Record<string, string | number> = { stage }
    if (valueCents !== undefined) params.value_cents = valueCents
    router.post(`/crm/opportunities/${oppId}/stage`, params, { preserveScroll: true })
  }

  const onDrop = (event: React.DragEvent<HTMLElement>, stage: string) => {
    event.preventDefault()
    const value = event.dataTransfer.getData("text/opportunity-id")
    const oppId = Number(value)
    if (!Number.isFinite(oppId)) return
    if (stage === "closed_won") {
      setDealValue("")
      setPendingDrop({ oppId, stage })
      return
    }
    moveCard(oppId, stage)
  }

  const toggleStatusFilter = (action: string) => {
    const newShowParked = action === "parked" ? (filters.show_parked ? undefined : "1") : (filters.show_parked ? "1" : undefined);
    const newShowClosed = (action === "dead" || action === "lost") ? (filters.show_closed ? undefined : "1") : (filters.show_closed ? "1" : undefined);
    router.get("/crm/opportunities", {
      client_id: filters.client_id ?? undefined,
      q: query || undefined,
      show_parked: newShowParked,
      show_closed: newShowClosed,
    }, { preserveState: true, preserveScroll: true })
  }

  return (
    <>
      <Head title="Opportunities">
        <meta name="description" content={description} />
        <meta property="og:title" content="Opportunities" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Opportunities</h1>
            <p className="mt-2">Track opportunities from warm reply through onboarding and delivery.</p>
          </header>

          <section className="rounded-lg border border-hairline bg-page p-4">
            <form onSubmit={submitFilters} className="grid gap-3 lg:grid-cols-[1fr_220px_auto_auto_auto_auto]">
              <label className="space-y-2">
                Search
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                  <Input value={query} onChange={(event) => setQuery(event.target.value)} className="pl-9" placeholder="name, contact email, or account" />
                </div>
              </label>
              <label className="space-y-2">
                Client
                <Select
                  value={filters.client_id ?? ""}
                  onChange={(event) => router.get("/crm/opportunities", {
                    client_id: event.target.value || undefined,
                    q: query || undefined,
                    show_parked: filters.show_parked ? "1" : undefined,
                    show_closed: filters.show_closed ? "1" : undefined,
                  })}
                >
                  <option value="">All clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.display_name}</option>
                  ))}
                </Select>
              </label>
              <Button type="submit">Search</Button>
              <Button
                type="button"
                variant={filters.show_parked ? "primary" : "secondary"}
                onClick={() => toggleStatusFilter("parked")}
              >
                Show parked ({status_counts.parked})
              </Button>
              <Button
                type="button"
                variant={filters.show_closed ? "primary" : "secondary"}
                onClick={() => toggleStatusFilter("dead")}
              >
                <Ghost className="h-4 w-4" />
                Show dead ({status_counts.dead})
              </Button>
              <Button
                type="button"
                variant={filters.show_closed ? "primary" : "secondary"}
                onClick={() => toggleStatusFilter("lost")}
              >
                <Archive className="h-4 w-4" />
                Show lost ({status_counts.lost})
              </Button>
            </form>
          </section>

          <div className="flex gap-4 overflow-x-auto pb-3">
            {columns.map((column) => (
              <section
                key={column.key}
                className="min-h-[520px] w-[300px] shrink-0 rounded-lg border border-hairline bg-surface p-3"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => onDrop(event, column.key)}
              >
                <header className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <h2>{column.label}</h2>
                  </div>
                  <Badge tone="neutral">{column.cards.length}</Badge>
                </header>
                <div className="space-y-3">
                  {column.cards.map((card) => <KanbanCard key={card.id} card={card} />)}
                </div>
              </section>
            ))}
          </div>
        </div>
      </AppShell>

      <Dialog open={pendingDrop !== null} onOpenChange={(open) => !open && setPendingDrop(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deal value</DialogTitle>
            <DialogDescription>Enter the deal value before moving to Closed-Won.</DialogDescription>
          </DialogHeader>
          <label htmlFor="deal_value">Value (USD)</label>
          <Input
            id="deal_value"
            type="number"
            min="0"
            step="1"
            value={dealValue}
            onChange={(event) => setDealValue(event.target.value)}
            placeholder="e.g. 5000"
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setPendingDrop(null)}>Cancel</Button>
            <Button
              type="button"
              onClick={() => {
                if (!pendingDrop) return
                const dollars = parseFloat(dealValue) || 0
                moveCard(pendingDrop.oppId, pendingDrop.stage, Math.round(dollars * 100))
                setDealValue("")
                setPendingDrop(null)
              }}
            >
              Move to Closed-Won
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function KanbanCard({ card }: { readonly card: OpportunityCard }) {
  const formattedValue = card.value_cents > 0
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: card.currency.toUpperCase() }).format(card.value_cents / 100)
    : null

  const serviceLineBadge = card.service_line === "leadgen"
    ? <Badge tone="signal">{card.service_line}</Badge>
    : <Badge tone="accent">{card.service_line}</Badge>

  return (
    <article
      draggable
      onDragStart={(event) => event.dataTransfer.setData("text/opportunity-id", String(card.id))}
      className={cn(
        "cursor-grab rounded-lg border border-hairline bg-page p-3 active:cursor-grabbing",
        card.won && "border-accent",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link href={`/crm/opportunities/${card.id}`} className="truncate no-underline hover:underline">
            <h3 className="truncate">{card.name}</h3>
          </Link>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {card.won && <Badge tone="accent">Won</Badge>}
          <div draggable={false} onDragStart={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="secondary" size="sm" aria-label="Actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {card.status !== "parked" && (
                  <DropdownMenuItem onSelect={() => router.post(`/crm/opportunities/${card.id}/status`, { status: "parked" }, { preserveScroll: true })}>
                    Park
                  </DropdownMenuItem>
                )}
                {card.status !== "active" && (
                  <DropdownMenuItem onSelect={() => router.post(`/crm/opportunities/${card.id}/status`, { status: "active" }, { preserveScroll: true })}>
                    Reactivate
                  </DropdownMenuItem>
                )}
                {card.status !== "lost" && (
                  <DropdownMenuItem onSelect={() => router.post(`/crm/opportunities/${card.id}/status`, { status: "lost" }, { preserveScroll: true })}>
                    Mark lost
                  </DropdownMenuItem>
                )}
                {card.status !== "dead" && (
                  <DropdownMenuItem onSelect={() => router.post(`/crm/opportunities/${card.id}/status`, { status: "dead" }, { preserveScroll: true })}>
                    Mark dead
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {card.contact && (
        <p className="mt-1 truncate text-sm text-ink-muted">{card.contact.email}</p>
      )}
      {card.account && (
        <p className="mt-0.5 truncate text-xs text-ink-muted">{card.account.domain}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-full border border-hairline"
            style={{ backgroundColor: card.client.color_hex }}
          />
          <span className="text-sm text-ink-muted">{card.client.display_name}</span>
        </span>
        {formattedValue && <Badge tone="neutral">{formattedValue}</Badge>}
        {serviceLineBadge}
        <Badge tone="muted" className="font-mono text-xs">{card.lifecycle_stage_id}</Badge>
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-ink-muted">
        <span>Stage {card.stage_age_days ?? 0}d</span>
        {card.open_task_count > 0 && <span>{card.open_task_count} open tasks</span>}
      </div>
    </article>
  )
}
